import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Phone, 
    Video as VideoIcon,
    FileText,
    Clock,
    Check,
    Zap,
    Calendar,
    DollarSign,
    ExternalLink,
    AlertCircle,
    Package,
    RefreshCw,
    MessageCircle,
    Copy,
    Mail
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Square Payment Links - Coaching Sessions
const COACHING_LINKS = {
    google_meet_1hr: 'https://square.link/u/gRAOiTri',
    google_meet_2hr: 'https://square.link/u/dnI6tMyL',
    phone_1hr: 'https://checkout.square.site/merchant/ML0799N4E2KRS/checkout/N6XTNEGNH7GJKVXLFFKYEJBU',
    phone_2hr: 'https://checkout.square.site/merchant/ML0799N4E2KRS/checkout/GYERLPQN4REEZVGFHBQIQOKQ',
};

// Square Payment Links - Resume Services
const RESUME_LINKS = {
    new_with_pdf: 'https://checkout.square.site/merchant/ML0799N4E2KRS/checkout/PVGFFKDIBYY4CTBUXHVGV3N2',
    new_no_pdf: 'https://square.link/u/VieSFc2i',
    add_airline: 'https://square.link/u/6flHRa8I',
    reedit: 'https://square.link/u/EO2Ske8M',
    expedited: 'https://square.link/u/xbbYpR2s',
};

// Square Payment Links - Combos
const COMBO_LINKS = {
    phone_resume: 'https://checkout.square.site/merchant/ML0799N4E2KRS/checkout/X7GT6ER4R4W5HLALR3O76F57',
    zoom_resume: 'https://square.link/u/BEoNqfMo',
};

// Calendly Link
const CALENDLY_LINK = 'https://calendly.com/flywithshelly/book-one-on-one-zoom-private-live-session-60-m-clone';

export default function Coaching() {
    const { user } = useAuth();
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [selectedResumeService, setSelectedResumeService] = useState(null);

    const SUBMISSION_EMAIL = 'flywithshelly@gmail.com';

    const copyEmailToClipboard = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(SUBMISSION_EMAIL);
            } else {
                // Fallback for older browsers / non-HTTPS
                const textArea = document.createElement('textarea');
                textArea.value = SUBMISSION_EMAIL;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            toast.success('Email copied to clipboard!', {
                description: `Paste ${SUBMISSION_EMAIL} into your mail app.`,
            });
        } catch (err) {
            toast.error('Could not copy. Please manually copy: ' + SUBMISSION_EMAIL);
        }
    };

    // Coaching Session Options
    const coachingSessions = [
        {
            id: 'google_meet_1hr',
            type: 'Google Meet',
            duration: '1 Hour',
            price: 150,
            icon: VideoIcon,
            iconBg: 'bg-blue-500/10',
            iconColor: 'text-blue-600',
            description: 'Video coaching session via Google Meet',
        },
        {
            id: 'google_meet_2hr',
            type: 'Google Meet',
            duration: '2 Hours',
            price: 250,
            icon: VideoIcon,
            iconBg: 'bg-blue-500/10',
            iconColor: 'text-blue-600',
            description: 'Extended video coaching session',
        },
        {
            id: 'phone_1hr',
            type: 'Phone',
            duration: '1 Hour',
            price: 120,
            icon: Phone,
            iconBg: 'bg-green-500/10',
            iconColor: 'text-green-600',
            description: 'Phone coaching session',
        },
        {
            id: 'phone_2hr',
            type: 'Phone',
            duration: '2 Hours',
            price: 220,
            icon: Phone,
            iconBg: 'bg-green-500/10',
            iconColor: 'text-green-600',
            description: 'Extended phone coaching session',
        },
    ];

    // Resume Services
    const resumeServices = [
        {
            id: 'new_with_pdf',
            name: 'New Resume (with PDF)',
            description: 'Full service resume for one airline - you provide your existing PDF',
            price: 100,
            expedited: 50,
            icon: FileText,
            iconBg: 'bg-green-500/10',
            iconColor: 'text-green-600',
            requiresPdf: true,
        },
        {
            id: 'new_no_pdf',
            name: 'New Resume (from scratch)',
            description: 'We build your resume from scratch - no PDF needed',
            price: 150,
            expedited: 50,
            icon: FileText,
            iconBg: 'bg-blue-500/10',
            iconColor: 'text-blue-600',
            requiresPdf: false,
        },
        {
            id: 'add_airline',
            name: 'Add/Modify Airline',
            description: 'Add or modify one airline on your existing resume',
            price: 75,
            expedited: 50,
            icon: FileText,
            iconBg: 'bg-purple-500/10',
            iconColor: 'text-purple-600',
            requiresPdf: true,
        },
        {
            id: 'reedit',
            name: 'Re-edit Resume',
            description: 'Minor edits for returning customers only',
            price: 55,
            expedited: 50,
            icon: RefreshCw,
            iconBg: 'bg-amber-500/10',
            iconColor: 'text-amber-600',
            requiresPdf: true,
            badge: 'Returning Customers',
        },
    ];

    // Combo Packages
    const combos = [
        {
            id: 'phone_resume',
            name: '1hr Phone Session + Resume (PDF)',
            price: 200,
            savings: 20,
            link: COMBO_LINKS.phone_resume,
        },
        {
            id: 'zoom_resume',
            name: '1hr Google Meet + Resume (PDF)',
            price: 225,
            savings: 25,
            link: COMBO_LINKS.zoom_resume,
        },
    ];

    const handleCoachingPurchase = (sessionId) => {
        const link = COACHING_LINKS[sessionId];
        if (link) {
            window.open(link, '_blank');
        }
    };

    const openResumeModal = (service) => {
        setSelectedResumeService(service);
        setShowResumeModal(true);
    };

    const handleResumeCheckout = () => {
        const link = RESUME_LINKS[selectedResumeService.id];
        if (link) {
            window.open(link, '_blank');
            setShowResumeModal(false);
        }
    };

    const handleComboPurchase = (link) => {
        window.open(link, '_blank');
    };

    const openCalendly = () => {
        window.open(CALENDLY_LINK, '_blank');
    };

    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Badge className="mb-4 bg-primary/10 text-primary">Private Services</Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Coaching & Resume Services
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        One-on-one coaching sessions and professional resume services to help you land your dream job
                    </p>
                </motion.div>

                {/* Warning Banner */}
                <Card className="p-4 mb-8 border-amber-500 bg-amber-50 dark:bg-amber-900/20">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-amber-800 dark:text-amber-200">Important Notice</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                                All sales are final. No refunds will be issued for coaching sessions or resume services. 
                                Please ensure you are committed before purchasing.
                            </p>
                        </div>
                    </div>
                </Card>

                <Tabs defaultValue="coaching" className="w-full">
                    <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-8">
                        <TabsTrigger value="coaching">Coaching</TabsTrigger>
                        <TabsTrigger value="resume">Resume</TabsTrigger>
                        <TabsTrigger value="combos">Combos</TabsTrigger>
                    </TabsList>

                    {/* Coaching Tab */}
                    <TabsContent value="coaching">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {/* Calendly CTA */}
                            <Card className="p-6 mb-8 border-primary/30 bg-primary/5">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                            <Calendar className="h-7 w-7 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading text-lg font-semibold">Book Your Session</h3>
                                            <p className="text-sm text-muted-foreground">
                                                Choose your date and time on Calendly, then complete payment
                                            </p>
                                        </div>
                                    </div>
                                    <Button onClick={openCalendly} className="gap-2 rounded-full">
                                        <ExternalLink className="h-4 w-4" />
                                        Open Calendly
                                    </Button>
                                </div>
                            </Card>

                            {/* Session Cards */}
                            <div className="grid md:grid-cols-2 gap-6">
                                {coachingSessions.map((session, index) => {
                                    const Icon = session.icon;
                                    return (
                                        <motion.div
                                            key={session.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Card className="p-6 h-full flex flex-col">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className={`h-12 w-12 rounded-xl ${session.iconBg} flex items-center justify-center`}>
                                                        <Icon className={`h-6 w-6 ${session.iconColor}`} />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-heading text-lg font-semibold">
                                                            {session.type} Session
                                                        </h3>
                                                        <p className="text-sm text-muted-foreground">{session.duration}</p>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-4 flex-1">
                                                    {session.description}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-2xl font-bold text-primary">
                                                        ${session.price}
                                                    </span>
                                                    <Button 
                                                        onClick={() => handleCoachingPurchase(session.id)}
                                                        className="rounded-full gap-2"
                                                        data-testid={`coaching-${session.id}-btn`}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                        Book Now
                                                    </Button>
                                                </div>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* VIP Discount Note */}
                            <Card className="p-4 mt-6 bg-purple-50 dark:bg-purple-900/20 border-purple-200">
                                <p className="text-sm text-center text-purple-700 dark:text-purple-300">
                                    <strong>VIP Members:</strong> Enjoy 20% off all 1:1 coaching sessions!{' '}
                                    <a 
                                        href="/membership" 
                                        className="underline font-semibold hover:text-purple-900 dark:hover:text-purple-100"
                                    >
                                        Join VIP tier to receive these perks.
                                    </a>
                                </p>
                            </Card>
                        </motion.div>
                    </TabsContent>

                    {/* Resume Tab */}
                    <TabsContent value="resume">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {/* Expedited Service - Separate Purchase */}
                            <Card className="p-4 mb-6 border-amber-500 bg-amber-50 dark:bg-amber-900/20">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                            <Zap className="h-6 w-6 text-amber-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                                                Expedited Service - $50
                                            </h3>
                                            <p className="text-sm text-amber-700 dark:text-amber-300">
                                                Need it fast? Add expedited service to get your resume within 24 hours
                                            </p>
                                        </div>
                                    </div>
                                    <Button 
                                        onClick={() => window.open(RESUME_LINKS.expedited, '_blank')}
                                        className="rounded-full gap-2 bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                                    >
                                        <Zap className="h-4 w-4" />
                                        Add Expedited - $50
                                    </Button>
                                </div>
                            </Card>

                            {/* Resume Upload Info */}
                            <Card className="p-6 mb-6 border-primary/20 bg-gradient-to-r from-stone-50 to-stone-100 dark:from-stone-900 dark:to-stone-800">
                                <div className="text-center mb-5">
                                    <div className="flex justify-center mb-3">
                                        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Mail className="h-7 w-7 text-primary" />
                                        </div>
                                    </div>
                                    <h3 className="font-heading text-xl font-semibold mb-2">
                                        How to Submit Your Resume
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        After completing your purchase, email your resume (with order confirmation #) to:
                                    </p>
                                </div>

                                {/* Large readable email with copy button */}
                                <div className="max-w-md mx-auto">
                                    <div className="flex items-center gap-2 p-3 bg-white dark:bg-stone-900 rounded-lg border-2 border-primary/30 mb-3">
                                        <Mail className="h-5 w-5 text-primary shrink-0 ml-1" />
                                        <span className="flex-1 font-mono text-sm sm:text-base font-semibold text-foreground select-all truncate">
                                            {SUBMISSION_EMAIL}
                                        </span>
                                        <Button
                                            size="sm"
                                            onClick={copyEmailToClipboard}
                                            className="rounded-md gap-1 shrink-0"
                                            data-testid="resume-copy-email-btn"
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                            Copy
                                        </Button>
                                    </div>
                                    <a
                                        href={`mailto:${SUBMISSION_EMAIL}?subject=Resume%20Submission`}
                                        className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2.5 text-sm font-semibold transition-colors no-underline"
                                        data-testid="resume-email-link"
                                    >
                                        <Mail className="h-4 w-4" />
                                        Open in Mail App
                                    </a>
                                </div>
                                <p className="text-xs text-center text-muted-foreground mt-4">
                                    Tip: If "Open in Mail App" doesn't work on your device, just tap <strong>Copy</strong> and paste the email address into Gmail, Outlook, or your preferred mail app.
                                </p>
                            </Card>

                            {/* Resume Service Cards */}
                            <div className="grid md:grid-cols-2 gap-6">
                                {resumeServices.map((service, index) => {
                                    const Icon = service.icon;
                                    return (
                                        <motion.div
                                            key={service.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Card className="p-6 h-full flex flex-col relative">
                                                {service.badge && (
                                                    <Badge className="absolute -top-2 right-4 bg-amber-500 text-white">
                                                        {service.badge}
                                                    </Badge>
                                                )}
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className={`h-12 w-12 rounded-xl ${service.iconBg} flex items-center justify-center`}>
                                                        <Icon className={`h-6 w-6 ${service.iconColor}`} />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-heading text-lg font-semibold">
                                                            {service.name}
                                                        </h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            3-5 business days
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-4 flex-1">
                                                    {service.description}
                                                </p>
                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="text-2xl font-bold text-primary">${service.price}</span>
                                                    <span className="text-sm text-muted-foreground">3-5 business days</span>
                                                </div>
                                                <Button 
                                                    onClick={() => openResumeModal(service)}
                                                    className="w-full rounded-full gap-2"
                                                    data-testid={`resume-${service.id}-btn`}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    Order Now - ${service.price}
                                                </Button>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </TabsContent>

                    {/* Combos Tab */}
                    <TabsContent value="combos">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="text-center mb-8">
                                <Badge className="bg-green-500 text-white mb-2">Save Money</Badge>
                                <h2 className="font-heading text-2xl font-bold">Bundle & Save</h2>
                                <p className="text-muted-foreground">
                                    Combine coaching with resume services for the best value
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                                {combos.map((combo, index) => (
                                    <motion.div
                                        key={combo.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <Card className="p-6 border-green-200 bg-green-50/50 dark:bg-green-900/20">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                                                    <Package className="h-6 w-6 text-green-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-heading font-semibold">{combo.name}</h3>
                                                    <Badge className="bg-green-500 text-white text-xs">
                                                        Save ${combo.savings}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-2xl font-bold text-primary">
                                                    ${combo.price}
                                                </span>
                                                <Button 
                                                    onClick={() => handleComboPurchase(combo.link)}
                                                    className="rounded-full gap-2 bg-green-600 hover:bg-green-700"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    Get Bundle
                                                </Button>
                                            </div>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Email Submission Info for Combos */}
                            <Card className="p-6 mt-8 max-w-3xl mx-auto border-primary/20 bg-gradient-to-r from-stone-50 to-stone-100 dark:from-stone-900 dark:to-stone-800">
                                <div className="text-center mb-5">
                                    <div className="flex justify-center mb-3">
                                        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Mail className="h-7 w-7 text-primary" />
                                        </div>
                                    </div>
                                    <h3 className="font-heading text-xl font-semibold mb-2">
                                        How to Submit Your Resume
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        After completing your combo purchase, email your resume (with order confirmation #) to:
                                    </p>
                                </div>
                                <div className="max-w-md mx-auto">
                                    <div className="flex items-center gap-2 p-3 bg-white dark:bg-stone-900 rounded-lg border-2 border-primary/30 mb-3">
                                        <Mail className="h-5 w-5 text-primary shrink-0 ml-1" />
                                        <span className="flex-1 font-mono text-sm sm:text-base font-semibold text-foreground select-all truncate">
                                            {SUBMISSION_EMAIL}
                                        </span>
                                        <Button
                                            size="sm"
                                            onClick={copyEmailToClipboard}
                                            className="rounded-md gap-1 shrink-0"
                                            data-testid="combo-copy-email-btn"
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                            Copy
                                        </Button>
                                    </div>
                                    <a
                                        href={`mailto:${SUBMISSION_EMAIL}?subject=Resume%20Submission%20-%20Combo`}
                                        className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2.5 text-sm font-semibold transition-colors no-underline"
                                        data-testid="combo-resume-email-link"
                                    >
                                        <Mail className="h-4 w-4" />
                                        Open in Mail App
                                    </a>
                                </div>
                                <p className="text-xs text-center text-muted-foreground mt-4">
                                    Tip: If "Open in Mail App" doesn't work, just tap <strong>Copy</strong> and paste into Gmail, Outlook, or your preferred mail app.
                                </p>
                            </Card>
                        </motion.div>
                    </TabsContent>
                </Tabs>

                {/* Resume Order Modal */}
                <Dialog open={showResumeModal} onOpenChange={setShowResumeModal}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Order: {selectedResumeService?.name}</DialogTitle>
                            <DialogDescription>
                                Complete your order via our secure checkout
                            </DialogDescription>
                        </DialogHeader>
                        {selectedResumeService && (
                            <div className="space-y-4">
                                <div className="bg-muted/50 rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-medium">{selectedResumeService.name}</span>
                                        <span className="text-2xl font-bold text-primary">${selectedResumeService.price}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedResumeService.description}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Delivery: 3-5 business days
                                    </p>
                                </div>

                                {/* Resume Submission Info */}
                                {selectedResumeService.requiresPdf && (
                                    <div className="bg-stone-50 dark:bg-stone-900/50 border rounded-lg p-4">
                                        <p className="font-medium mb-3 flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-primary" />
                                            Email Your Resume After Checkout
                                        </p>
                                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-stone-900 rounded-md border mb-2">
                                            <span className="flex-1 font-mono text-sm font-semibold select-all truncate">
                                                {SUBMISSION_EMAIL}
                                            </span>
                                            <Button
                                                size="sm"
                                                onClick={copyEmailToClipboard}
                                                className="rounded-md gap-1 shrink-0"
                                                data-testid="modal-copy-email-btn"
                                            >
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy
                                            </Button>
                                        </div>
                                        <a
                                            href={`mailto:${SUBMISSION_EMAIL}?subject=Resume%20Submission`}
                                            className="inline-flex items-center justify-center gap-2 w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-3 py-2 text-sm font-semibold transition-colors no-underline"
                                            data-testid="modal-resume-email-link"
                                        >
                                            <Mail className="h-4 w-4" />
                                            Open in Mail App
                                        </a>
                                    </div>
                                )}

                                {/* Expedited Note */}
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                                        <Zap className="h-4 w-4" />
                                        <span>Need it faster? Purchase <strong>Expedited Service ($50)</strong> separately for 24-hour delivery.</span>
                                    </p>
                                </div>

                                <Button 
                                    onClick={() => handleResumeCheckout()}
                                    className="w-full rounded-full gap-2"
                                >
                                    <DollarSign className="h-4 w-4" />
                                    Checkout - ${selectedResumeService.price}
                                </Button>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
